import { useCallback, useEffect, useState } from 'react'
import { Compteur } from '../Compteur'
import {
  ajouterMarchand,
  appliquerTauxATousLesArticles,
  creerCompteMarchand,
  definirBlocageMarchand,
  listerCommissions,
  listerMarchands,
  listerRubriques,
  lireReglagesMarchand,
  enregistrerReglagesMarchand,
  modifierFicheMarchand,
  modifierTauxArticle,
  resumeCommissions,
  type CommissionArticle,
  type Rubrique,
  type Identifiants,
  type ResumeCommission,
  type ReglagesMarchand,
} from '../../api/dispatch'
import { lireMinimumCommande, lireTauxCommission } from '../../api/tarifs'
import type { Marchand } from '../../api/types'
import { TYPES_MARCHAND } from '../../api/typeMarchand'
import { dateCourte, fcfa } from '../Format'
import { Modale, Panneau } from '../Modale'
import { BlocIdentifiants } from '../BlocIdentifiants'
import { ChoixPosition } from '../ChoixPosition'
import { accesARemettre } from '../../api/identifiants'
import { contactAffiche, validerContact, type ContactValide } from '../../api/contactCompte'
import { ChampTelephone } from '../../../../../../web-commun/connexion-whatsapp'
import { useTelephone } from '../useTelephone'
import type { Casquette } from '../../api/discussions'

/**
 * Marchands — TRANSPOSITION du gabarit d'origine.
 *
 * Manquaient : la création d'établissement, la fiche en panneau, le détail des
 * commissions article par article, la suspension motivée et le rattrapage de
 * compte. Autrement dit tout ce que le dispatch fait réellement ici.
 */
const pourcent = (v: number) => `${(Math.round(v * 1000) / 10).toString().replace('.', ',')} %`

/** Ligne de fiche — reprise au style près du gabarit (bordure basse, 13 px). */
const Row = ({ libelle, valeur }: { libelle: string; valeur: React.ReactNode }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '9px 0',
      borderBottom: '1px solid var(--n200)',
      fontSize: 13,
    }}
  >
    <span style={{ color: 'var(--n400)' }}>{libelle}</span>
    <span style={{ fontWeight: 600, textAlign: 'right' }}>{valeur || '—'}</span>
  </div>
)

export function EcranMarchands({ surContacter }: { surContacter: (pairId: string, casquette: Casquette) => void }) {
  const [marchands, setMarchands] = useState<Marchand[]>([])
  const [commissions, setCommissions] = useState<Record<string, ResumeCommission>>({})
  const [erreur, setErreur] = useState<string | null>(null)
  const [fiche, setFiche] = useState<Marchand | null>(null)
  const [creation, setCreation] = useState(false)
  const [identifiants, setIdentifiants] = useState<(Identifiants & { nom: string }) | null>(null)
  const [echecCompte, setEchecCompte] = useState<{ nom: string; cause: string } | null>(null)
  /* Établissement dont on (re)crée le compte du gérant — fenêtre à e-mail ET numéro WhatsApp. */
  const [compteAcreer, setCompteAcreer] = useState<Marchand | null>(null)

  const charger = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([listerMarchands(), resumeCommissions()])
      setMarchands(m)
      setCommissions(c)
      setErreur(null)
      return m
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture impossible.')
      return []
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  const ouverts = marchands.filter((m) => m.etat === 'ouvert').length
  const bloques = marchands.filter((m) => m.etat === 'bloque').length

  async function basculerSuspension(m: Marchand) {
    if (m.etat === 'bloque') {
      if (
        !window.confirm(
          `Lever la suspension de « ${m.nom} » ?\n\nIl redeviendra ouvert selon ses propres horaires.`,
        )
      )
        return
      await definirBlocageMarchand(m.id, null)
    } else {
      /* Le motif est OBLIGATOIRE : il s'affiche tel quel dans l'application du
         marchand. Suspendre sans motif le laisserait fermé sans savoir
         pourquoi, et sans pouvoir y remédier — il ne peut pas lever ça. */
      const motif = (
        window.prompt(
          `Suspendre « ${m.nom} ».\n\nMotif — le marchand le lira dans son application :\n(ex. suspicion de rupture de la chaîne du froid)`,
        ) ?? ''
      ).trim()
      if (!motif) {
        window.alert('Un motif est nécessaire : le marchand doit savoir pourquoi il est fermé.')
        return
      }
      await definirBlocageMarchand(m.id, motif)
    }
    await charger()
    setFiche(null)
  }

  /** Rattrapage : la fiche existe, le compte non. Sans ça, le seul recours
      était de créer un doublon d'établissement. */
  function retenterCompte(m: Marchand) {
    setFiche(null)
    setCompteAcreer(m)
  }

  return (
    <>
      <div className="stats">
        <div className="stat">
          <div className="label">Total marchands</div>
          <div className="val"><Compteur valeur={String(marchands.length)} /></div>
        </div>
        <div className="stat">
          <div className="label">Ouverts</div>
          <div className="val" style={{ color: 'var(--green)' }}>
            <Compteur valeur={String(ouverts)} />
          </div>
        </div>
        <div className="stat" title="Hors de leurs horaires, ou en pause">
          <div className="label">Fermés maintenant</div>
          <div className="val" style={{ color: 'var(--n400)' }}>
            <Compteur valeur={String(marchands.length - ouverts - bloques)} />
          </div>
        </div>
        <div className="stat">
          <div className="label">Suspendus par nous</div>
          <div className="val" style={{ color: 'var(--danger)' }}>
            <Compteur valeur={String(bloques)} />
          </div>
        </div>
      </div>

      {erreur && <p style={{ color: 'var(--danger)', fontSize: 13 }}>{erreur}</p>}

      <div className="card">
        <div className="card-header">
          Marchands
          <div className="spacer" style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: 'var(--n400)', fontWeight: 500 }}>
            Créés ici — identifiant de connexion = <b>e-mail du gérant</b>, code temporaire généré à
            la validation (modifiable dans l'app Marchand)
          </span>
          <button
            className="btn btn-accent btn-sm"
            style={{ marginLeft: 12 }}
            onClick={() => setCreation(true)}
          >
            + Nouveau marchand
          </button>
        </div>
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Type</th>
                <th>Quartier</th>
                <th>Identifiant (e-mail)</th>
                <th>Téléphone</th>
                <th>Statut</th>
                <th>Commission</th>
                <th>Note</th>
                <th>Créé le</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {marchands.map((m) => {
                const c = commissions[m.id]
                return (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 600 }}>
                      {m.nom}
                      {/* Sans point de retrait, la livraison est facturée au
                          palier le moins cher et le livreur ne sait pas où aller. */}
                      {!m.positionConnue && (
                        <span className="badge badge-red" style={{ marginLeft: 6 }}>
                          position manquante
                        </span>
                      )}
                    </td>
                    <td>{m.type}</td>
                    <td>{m.quartier}</td>
                    {/* L'identifiant vient de `profiles`, pas de `merchants.email`
                        qui est vide partout. */}
                    <td>
                      {m.proprietaireId
                        ? contactAffiche(m.email, m.telephoneGerant) || '—'
                        : <span style={{ color: 'var(--warning)' }}>aucun compte</span>}
                    </td>
                    <td>{m.telephone || '—'}</td>
                    <td>
                      <span
                        className={`badge badge-${m.etat === 'ouvert' ? 'green' : m.etat === 'bloque' ? 'red' : 'gray'}`}
                        title={m.motifBlocage ?? undefined}
                      >
                        {m.etat === 'ouvert' ? 'ouvert' : m.etat === 'bloque' ? 'suspendu' : 'fermé'}
                      </span>
                    </td>
                    {/* Moyenne PONDÉRÉE par le prix, plus l'étendue : un taux
                        moyen simple donnerait le même poids à un plat à 500 F
                        et à un menu à 8 000 F. */}
                    <td style={{ color: 'var(--n400)' }}>
                      {!c || !c.nb ? (
                        <span style={{ color: 'var(--n400)' }}>aucun article</span>
                      ) : (
                        <>
                          <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
                            {pourcent(c.moyenne)}
                          </span>
                          {c.min !== c.max && (
                            <span style={{ fontSize: 10.5, color: 'var(--n400)' }}>
                              {' '}
                              · {pourcent(c.min)}–{pourcent(c.max)}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td>{Number(m.note ?? 0)}/5</td>
                    <td>{dateCourte(m.creeLe)}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => setFiche(m)}>
                        Détails
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Panneau titre={fiche?.nom ?? ''} ouvert={Boolean(fiche)} surFermer={() => setFiche(null)}>
        {fiche && (
          <>
            <div
              style={{
                fontSize: 11,
                color: 'var(--n400)',
                textTransform: 'uppercase',
                letterSpacing: '.5px',
                marginBottom: 6,
              }}
            >
              Fiche établissement (créée ici — dispatch)
            </div>

            {fiche.proprietaireId ? (
              <button
                className="btn btn-accent btn-sm"
                style={{ marginBottom: 10 }}
                onClick={() => {
                  setFiche(null)
                  surContacter(fiche.proprietaireId!, 'marchand')
                }}
              >
                Contacter (chat)
              </button>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--n700)',
                  background: 'var(--rouge-p)',
                  border: '1px solid var(--danger)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  marginBottom: 10,
                }}
              >
                Cet établissement <b>n'a pas de compte de connexion</b> : le gérant ne peut pas
                ouvrir l'app Marchand, et le chat est indisponible.
                <button
                  className="btn btn-accent btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => void retenterCompte(fiche)}
                >
                  Créer le compte du gérant
                </button>
              </div>
            )}

            {fiche.proprietaireId ? (
              <Row
                libelle={`Identifiant de connexion (${fiche.email ? 'e-mail' : 'numéro WhatsApp'})`}
                valeur={contactAffiche(fiche.email, fiche.telephoneGerant)}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '9px 0',
                  borderBottom: '1px solid var(--n200)',
                  fontSize: 13,
                }}
              >
                <span style={{ color: 'var(--n400)' }}>Identifiant de connexion</span>
                <span style={{ fontWeight: 600, color: 'var(--warning)', textAlign: 'right' }}>
                  Aucun compte créé
                </span>
              </div>
            )}
            <ReglagesApp
              key={fiche.id}
              marchand={fiche}
              surEnregistre={async () => {
                const liste = await charger()
                setFiche(liste.find((x) => x.id === fiche.id) ?? null)
              }}
            />

            <Row libelle="Téléphone (contact)" valeur={fiche.telephone} />
            <Row libelle="Type" valeur={fiche.type} />
            <Row libelle="Quartier" valeur={fiche.quartier} />
            <Row libelle="Note" valeur={fiche.note ? `${fiche.note}/5` : ''} />
            <Row libelle="Cree le" valeur={dateCourte(fiche.creeLe)} />

            <div style={{ marginTop: 20, borderTop: '1px solid var(--n200)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <b style={{ fontSize: 13 }}>Commissions par article</b>
                <span style={{ fontSize: 11.5, color: 'var(--n400)' }}>
                  réglées par le marchand · lecture seule ici
                </span>
              </div>
              <CommissionsMarchand marchandId={fiche.id} />
            </div>

            <div style={{ marginTop: 20, borderTop: '1px solid var(--n200)', paddingTop: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--n400)', marginBottom: 8, lineHeight: 1.5 }}>
                L'ouverture suit les <b>horaires que le marchand a réglés</b> (7 jours), et sa pause
                éventuelle. Ci-dessous : suspension imposée par SwiftEats, que lui ne peut pas lever.
              </div>
              {fiche.motifBlocage && (
                <div
                  style={{
                    fontSize: 12,
                    background: 'var(--rouge-p)',
                    border: '1px solid var(--danger)',
                    color: 'var(--rouge)',
                    borderRadius: 8,
                    padding: '9px 11px',
                    marginBottom: 9,
                  }}
                >
                  <b>Suspendu.</b> Motif transmis au marchand : « {fiche.motifBlocage} »
                </div>
              )}
              <button
                className="btn btn-outline btn-sm"
                style={{
                  color: `var(--${fiche.etat === 'bloque' ? 'green' : 'danger'})`,
                  borderColor: `var(--${fiche.etat === 'bloque' ? 'green' : 'danger'})`,
                }}
                onClick={() => void basculerSuspension(fiche)}
              >
                {fiche.etat === 'bloque' ? 'Lever la suspension' : 'Suspendre cet établissement'}
              </button>
            </div>
          </>
        )}
      </Panneau>

      {creation && (
        <ModaleMarchand
          surFermer={() => setCreation(false)}
          surCreer={async (v) => {
            const m = await ajouterMarchand(v)
            setCreation(false)
            await charger()
            /* La fiche est posée ; le compte peut encore échouer. On le DIT —
               sinon la modale se fermait comme si tout allait bien et le
               gérant se retrouvait sans aucun moyen d'entrer. */
            try {
              const cred = await creerCompteMarchand({
                marchandId: m.id,
                contact: v.contact,
                nomGerant: v.nomGerant,
              })
              setIdentifiants({ email: v.contact.email, phone: v.contact.phone, ...cred, nom: m.name })
            } catch (e) {
              setEchecCompte({
                nom: m.name,
                cause: e instanceof Error ? e.message : 'Cause inconnue',
              })
            }
            await charger()
          }}
        />
      )}

      {identifiants && (
        <Modale titre="Compte marchand cree" ouverte surFermer={() => setIdentifiants(null)}>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <b>{identifiants.nom}</b> est cree.{' '}
            <BlocIdentifiants
              acces={accesARemettre(identifiants)}
              introduction="Transmettez ces accès au gérant — il se connecte à l'app Marchand puis change son mot de passe :"
            />
            <button className="btn btn-accent" onClick={() => setIdentifiants(null)}>
              Termine
            </button>
          </div>
        </Modale>
      )}

      {compteAcreer && (
        <ModaleCompteGerant
          marchand={compteAcreer}
          surFermer={() => setCompteAcreer(null)}
          surCreer={async (contact) => {
            const m = compteAcreer
            const cred = await creerCompteMarchand({ marchandId: m.id, contact, nomGerant: m.nomContact || m.nom })
            setCompteAcreer(null)
            setIdentifiants({ email: contact.email, phone: contact.phone, ...cred, nom: m.nom })
            await charger()
          }}
        />
      )}

      {echecCompte && (
        <Modale
          titre="Marchand créé — SANS compte de connexion"
          ouverte
          surFermer={() => setEchecCompte(null)}
        >
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            L'établissement <b>{echecCompte.nom}</b> est bien enregistré, mais la création du compte
            de connexion a échoué. <b>Le gérant ne peut pas se connecter en l'état.</b>
            <div
              style={{
                background: 'var(--rouge-p)',
                border: '1px solid var(--danger)',
                borderRadius: 8,
                padding: '12px 14px',
                margin: '12px 0',
                color: 'var(--rouge)',
              }}
            >
              {echecCompte.cause}
            </div>
            Ouvrez la fiche de l'établissement pour retenter la création du compte.
            <button
              className="btn btn-accent"
              style={{ marginTop: 8 }}
              onClick={() => setEchecCompte(null)}
            >
              Compris
            </button>
          </div>
        </Modale>
      )}
    </>
  )
}

/**
 * Commission d'un marchand (0055) : taux CONTRACTUEL de l'établissement, minimum de commande propre, et taux
 * article par article — tous décidés ICI (le marchand ne voit plus de champ de taux). Prix client = prix du
 * marchand × (1 + taux), arrondi 25 F, calculé par la base : la colonne « Prix client » le relit, jamais
 * recalculé dans la console. La moyenne est PONDÉRÉE par le prix.
 */
function CommissionsMarchand({ marchandId }: { marchandId: string }) {
  const [articles, setArticles] = useState<CommissionArticle[] | null>(null)
  const [reglages, setReglages] = useState<ReglagesMarchand | null>(null)
  const [tauxSaisi, setTauxSaisi] = useState('')
  const [minimumSaisi, setMinimumSaisi] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const [confirmerTous, setConfirmerTous] = useState(false)

  const recharger = useCallback(async () => {
    const [a, r] = await Promise.all([listerCommissions(marchandId), lireReglagesMarchand(marchandId)])
    setArticles(a)
    setReglages(r)
    setTauxSaisi(r.taux === null ? '' : String(Math.round(r.taux * 100)))
    setMinimumSaisi(r.minimumCommande === null ? '' : String(r.minimumCommande))
  }, [marchandId])

  useEffect(() => {
    let vivant = true
    setArticles(null)
    setErreur(null)
    setInfo(null)
    recharger().catch((e: unknown) => vivant && setErreur(e instanceof Error ? e.message : 'Articles illisibles'))
    return () => {
      vivant = false
    }
  }, [recharger])

  async function agir(action: () => Promise<string | null>) {
    setEnCours(true)
    setErreur(null)
    setInfo(null)
    try {
      setInfo(await action())
      await recharger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  function enregistrerReglages() {
    const t = lireTauxCommission(tauxSaisi)
    if (!t.ok) return setErreur(t.message)
    const m = lireMinimumCommande(minimumSaisi)
    if (!m.ok) return setErreur(m.message)
    void agir(async () => {
      await enregistrerReglagesMarchand(marchandId, { taux: t.taux, minimumCommande: m.valeur })
      return 'Réglages du marchand enregistrés. Les nouveaux articles prendront ce taux.'
    })
  }

  function appliquerATous() {
    const t = lireTauxCommission(tauxSaisi)
    if (!t.ok) return setErreur(t.message)
    setConfirmerTous(false)
    void agir(async () => {
      await enregistrerReglagesMarchand(marchandId, {
        taux: t.taux,
        minimumCommande: reglages?.minimumCommande ?? null,
      })
      const n = await appliquerTauxATousLesArticles(marchandId, t.taux)
      return `Taux de ${pourcent(t.taux)} appliqué à ${n} article${n > 1 ? 's' : ''}.`
    })
  }

  function changerTauxArticle(a: CommissionArticle, saisie: string) {
    if (saisie.trim() === String(Math.round(a.taux * 100))) return
    const t = lireTauxCommission(saisie)
    if (!t.ok) return setErreur(`${a.nom} : ${t.message}`)
    void agir(async () => {
      await modifierTauxArticle(a.id, t.taux)
      return `${a.nom} : taux passé à ${pourcent(t.taux)}.`
    })
  }

  if (erreur && !articles) return <div style={{ fontSize: 12, color: 'var(--danger)' }}>Articles illisibles : {erreur}</div>
  if (!articles || !reglages) return <div>Chargement…</div>

  const sans0055 = reglages.taux === null
  const taux = articles.map((a) => a.taux)
  const min = taux.length ? Math.min(...taux) : 0
  const max = taux.length ? Math.max(...taux) : 0
  const base = articles.reduce((s, a) => s + a.prix, 0)
  const moyenne = base > 0 ? articles.reduce((s, a) => s + a.prix * a.taux, 0) / base : 0

  return (
    <>
      {sans0055 ? (
        <p className="avis avis-info" style={{ marginTop: 0 }}>
          Taux contractuel et minimum de commande réglables après la mise à jour de la base (migration 0055).
        </p>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
          <div>
            <label>Taux contractuel (%)</label>
            <input className="champ-n" inputMode="numeric" value={tauxSaisi} onChange={(e) => setTauxSaisi(e.target.value)} disabled={enCours} />
          </div>
          <div>
            <label>Commande minimum propre (FCFA)</label>
            <input className="champ-n" inputMode="numeric" placeholder="plateforme" value={minimumSaisi} onChange={(e) => setMinimumSaisi(e.target.value)} disabled={enCours} />
          </div>
          <button className="btn btn-accent btn-sm" disabled={enCours} onClick={enregistrerReglages}>
            Enregistrer
          </button>
          <button className="btn btn-outline btn-sm" disabled={enCours || !articles.length} onClick={() => setConfirmerTous(true)}>
            Appliquer ce taux à tous les articles
          </button>
        </div>
      )}
      {confirmerTous && (
        <div className="avis avis-warn">
          Passer les {articles.length} article{articles.length > 1 ? 's' : ''} de ce marchand à {tauxSaisi || '?'} % ? Les
          prix client changent aussitôt (les commandes déjà passées gardent leur prix).{' '}
          <button className="btn btn-accent btn-sm" onClick={appliquerATous}>
            Confirmer
          </button>{' '}
          <button className="btn btn-outline btn-sm" onClick={() => setConfirmerTous(false)}>
            Annuler
          </button>
        </div>
      )}
      {erreur && <div style={{ fontSize: 12, color: 'var(--danger)', margin: '6px 0' }}>{erreur}</div>}
      {info && <div style={{ fontSize: 12, color: 'var(--vert)', margin: '6px 0' }}>{info}</div>}
      {!articles.length ? (
        <div style={{ fontSize: 12, color: 'var(--n400)' }}>Aucun article au menu — ce marchand n'a encore rien publié.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span className="badge badge-blue">
              {articles.length} article{articles.length > 1 ? 's' : ''}
            </span>
            <span className="badge badge-green">Moyenne pondérée {pourcent(moyenne)}</span>
            <span className={`badge ${min === max ? 'badge-gray' : 'badge-yellow'}`}>
              {min === max ? `Taux unique ${pourcent(min)}` : `De ${pourcent(min)} à ${pourcent(max)}`}
            </span>
          </div>
          <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--n200)', borderRadius: 10 }}>
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Article</th>
                  <th>Section</th>
                  <th style={{ textAlign: 'right' }}>Prix marchand</th>
                  <th style={{ textAlign: 'right' }}>Prix client</th>
                  <th style={{ textAlign: 'right' }}>Taux (%)</th>
                </tr>
              </thead>
              <tbody>
                {[...articles]
                  .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
                  .map((a) => (
                    <tr key={a.id} style={a.disponible ? undefined : { opacity: 0.5 }}>
                      <td>
                        {a.nom}
                        {!a.disponible && <span style={{ fontSize: 10, color: 'var(--n400)' }}> (indispo)</span>}
                      </td>
                      <td style={{ color: 'var(--n400)' }}>{a.section}</td>
                      <td style={{ textAlign: 'right' }}>{fcfa(a.prix)}</td>
                      <td style={{ textAlign: 'right' }}>{a.prixClient === null ? '—' : fcfa(a.prixClient)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <input
                          key={`${a.id}-${a.taux}`}
                          className="champ-n"
                          style={{ width: 60 }}
                          inputMode="numeric"
                          aria-label={`Taux de commission de ${a.nom}, en %`}
                          defaultValue={String(Math.round(a.taux * 100))}
                          disabled={enCours || sans0055}
                          onBlur={(e) => changerTauxArticle(a, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}

function ModaleMarchand({
  surFermer,
  surCreer,
}: {
  surFermer: () => void
  surCreer: (v: {
    nom: string
    type: string
    quartier: string
    /** Numéro du gérant en E.164 (contact de la fiche), ou '' s'il n'y en a pas. */
    telephone: string
    notes: string
    contact: ContactValide
    nomGerant: string
  }) => Promise<void>
}) {
  const [nom, setNom] = useState('')
  const [type, setType] = useState('restaurant')
  const [quartier, setQuartier] = useState('')
  const [prenomGerant, setPrenomGerant] = useState('')
  const [nomGerant, setNomGerant] = useState('')
  const [email, setEmail] = useState('')
  const [telephone, setTelephone] = useTelephone()
  const [champContact, setChampContact] = useState<'email' | 'telephone' | 'contact' | null>(null)
  const [notes, setNotes] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  async function creer() {
    if (!nom.trim()) {
      setErreur("Nom de l'etablissement requis.")
      return
    }
    if (!prenomGerant.trim() || !nomGerant.trim()) {
      setErreur('Prenom et nom du gerant requis.')
      return
    }
    /* Au moins un e-mail OU un numéro WhatsApp ; chacun, s'il est renseigné, doit être valide. */
    const contact = validerContact({ email, telephone })
    if (!contact.ok) {
      setErreur(contact.message)
      setChampContact(contact.champ)
      return
    }
    setChampContact(null)
    setEnvoi(true)
    setErreur(null)
    try {
      await surCreer({
        nom: nom.trim(),
        type,
        quartier,
        telephone: contact.contact.phone ?? '',
        notes,
        contact: contact.contact,
        nomGerant: `${prenomGerant.trim()} ${nomGerant.trim()}`,
      })
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Creation impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Modale titre="Nouveau marchand" ouverte surFermer={surFermer}>
      <label>Nom de l'etablissement</label>
      <input value={nom} onChange={(e) => setNom(e.target.value)} autoFocus />

      <label>Type</label>
      <select value={type} onChange={(e) => setType(e.target.value)}>
        {/* La valeur de chaque option EST une valeur de l'enum `merchant_kind`. */}
        {TYPES_MARCHAND.map((t) => (
          <option key={t.valeur} value={t.valeur}>
            {t.libelle}
          </option>
        ))}
      </select>

      <label>Quartier</label>
      <input value={quartier} onChange={(e) => setQuartier(e.target.value)} />

      <div
        style={{
          fontSize: 11.5,
          color: 'var(--n700)',
          background: 'var(--accent-light)',
          borderRadius: 8,
          padding: '9px 11px',
          margin: '8px 0',
        }}
      >
        Le <b>taux de commission contractuel</b> se règle dans la fiche du marchand, une fois créé (taux
        initial : onglet <b>Config</b>). Chaque article prend ce taux ; le client paie le prix du marchand
        plus la commission, le marchand reçoit son prix.
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label>Prenom du gerant</label>
          <input value={prenomGerant} onChange={(e) => setPrenomGerant(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Nom du gerant</label>
          <input value={nomGerant} onChange={(e) => setNomGerant(e.target.value)} />
        </div>
      </div>

      <label>E-mail du gérant (recommandé)</label>
      <input
        type="email"
        placeholder="gerant@exemple.ml"
        value={email}
        aria-invalid={champContact === 'email' || champContact === 'contact' ? true : undefined}
        onChange={(e) => setEmail(e.target.value)}
      />

      <ChampTelephone
        valeur={telephone}
        surChangement={setTelephone}
        etiquette="Numéro WhatsApp du gérant"
        montrerErreur={champContact === 'telephone'}
        aide="Au Mali, c'est souvent le plus pratique : le gérant reçoit un code sur WhatsApp à chaque connexion."
      />
      <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '4px 0 0' }}>
        Renseignez au moins l'un des deux : l'e-mail ou le numéro WhatsApp.
      </p>

      <label>Notes</label>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />

      <div
        style={{
          fontSize: 11.5,
          color: 'var(--n700)',
          background: 'var(--accent-light)',
          borderRadius: 6,
          padding: '8px 10px',
          marginTop: 8,
        }}
      >
        Un <b>compte unique</b> sera créé pour l'établissement. Avec un <b>e-mail</b>, le gérant se
        connecte à l'app Marchand avec un code temporaire (qu'il changera dans l'app) ; avec un
        <b> numéro WhatsApp</b> seul, il reçoit un code sur WhatsApp à chaque connexion.
      </div>

      {erreur && <p style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{erreur}</p>}

      <button
        className="btn btn-accent"
        style={{ marginTop: 16 }}
        disabled={envoi}
        onClick={() => void creer()}
      >
        {envoi ? 'Creation…' : 'Creer le marchand + le compte'}
      </button>
    </Modale>
  )
}

/**
 * (Re)création du compte du gérant d'un établissement qui n'en a pas : e-mail et/ou numéro WhatsApp.
 * Remplace l'ancienne boîte `window.prompt` (e-mail seul).
 */
function ModaleCompteGerant({
  marchand,
  surFermer,
  surCreer,
}: {
  marchand: Marchand
  surFermer: () => void
  surCreer: (contact: ContactValide) => Promise<void>
}) {
  const [email, setEmail] = useState(marchand.email)
  /* Le numéro de la fiche (contact de l'établissement) sert de point de départ. */
  const [telephone, setTelephone] = useTelephone(marchand.telephone ?? '')
  const [champContact, setChampContact] = useState<'email' | 'telephone' | 'contact' | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  async function creer() {
    const contact = validerContact({ email, telephone })
    if (!contact.ok) {
      setErreur(contact.message)
      setChampContact(contact.champ)
      return
    }
    setChampContact(null)
    setEnvoi(true)
    setErreur(null)
    try {
      await surCreer(contact.contact)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création du compte impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Modale titre={`Compte du gérant — ${marchand.nom}`} ouverte surFermer={surFermer}>
      <label>E-mail du gérant (recommandé)</label>
      <input
        type="email"
        placeholder="gerant@exemple.ml"
        value={email}
        autoFocus
        aria-invalid={champContact === 'email' || champContact === 'contact' ? true : undefined}
        onChange={(e) => setEmail(e.target.value)}
      />
      <ChampTelephone
        valeur={telephone}
        surChangement={setTelephone}
        etiquette="Numéro WhatsApp du gérant"
        montrerErreur={champContact === 'telephone'}
        aide="Au Mali, c'est souvent le plus pratique : le gérant reçoit un code sur WhatsApp à chaque connexion."
      />
      <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '4px 0 0' }}>
        Renseignez au moins l'un des deux : l'e-mail ou le numéro WhatsApp.
      </p>
      {erreur && <p style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{erreur}</p>}
      <button className="btn btn-accent" style={{ marginTop: 16 }} disabled={envoi} onClick={() => void creer()}>
        {envoi ? 'Création…' : 'Créer le compte du gérant'}
      </button>
    </Modale>
  )
}

/**
 * Ce que le dispatch règle pour l'app client : les rubriques de
 * l'établissement et son point de retrait.
 *
 * Avant cet écran, AUCUNE interface ne posait ni l'un ni l'autre : seuls les
 * 8 établissements du jeu de départ avaient des rubriques et une position. Un
 * marchand créé ici n'apparaissait sous aucune rubrique, et sans position
 * `create-order` comptait 0 km — palier de livraison le moins cher, livreur
 * sans adresse de retrait.
 */
function ReglagesApp({
  marchand,
  surEnregistre,
}: {
  marchand: Marchand
  surEnregistre: () => Promise<void>
}) {
  const [rubriques, setRubriques] = useState<Rubrique[]>([])
  const [choisies, setChoisies] = useState<number[]>(marchand.categories)
  const [point, setPoint] = useState<[number, number] | null>(
    marchand.positionConnue ? [marchand.lat, marchand.lng] : null,
  )
  const [enCours, setEnCours] = useState(false)
  const [message, setMessage] = useState<{ texte: string; erreur: boolean } | null>(null)

  useEffect(() => {
    listerRubriques().then(setRubriques).catch(() => setRubriques([]))
  }, [])

  const rubriquesModifiees =
    [...choisies].sort().join(',') !== [...marchand.categories].sort().join(',')
  const positionModifiee =
    point != null &&
    (!marchand.positionConnue || point[0] !== marchand.lat || point[1] !== marchand.lng)

  async function enregistrer() {
    setEnCours(true)
    setMessage(null)
    try {
      await modifierFicheMarchand(marchand.id, {
        ...(rubriquesModifiees ? { categories: choisies } : {}),
        ...(positionModifiee && point ? { lat: point[0], lng: point[1] } : {}),
      })
      setMessage({ texte: 'Enregistré — visible tout de suite dans l\'app client.', erreur: false })
      await surEnregistre()
    } catch (e) {
      setMessage({ texte: e instanceof Error ? e.message : 'Enregistrement impossible.', erreur: true })
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div style={{ margin: '4px 0 16px', paddingBottom: 14, borderBottom: '1px solid var(--n200)' }}>
      <b style={{ fontSize: 13 }}>Rubriques dans l'app client</b>
      <div style={{ fontSize: 11.5, color: 'var(--n400)', margin: '2px 0 8px' }}>
        Sans rubrique, l'établissement n'apparaît que sous « Tout ».
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {rubriques.map((r) => {
          const on = choisies.includes(r.id)
          return (
            <button
              key={r.id}
              type="button"
              className={`btn btn-sm ${on ? 'btn-accent' : 'btn-outline'}`}
              aria-pressed={on}
              onClick={() =>
                setChoisies((c) => (on ? c.filter((x) => x !== r.id) : [...c, r.id]))
              }
            >
              {r.nom}
            </button>
          )
        })}
      </div>

      <b style={{ fontSize: 13 }}>Point de retrait</b>
      <div style={{ fontSize: 11.5, color: 'var(--n400)', margin: '2px 0 8px' }}>
        Cliquez sur la carte à l'emplacement exact de l'établissement, puis ajustez l'épingle.
        C'est là que le livreur vient chercher la commande, et la distance qui fixe les frais de
        livraison part de ce point.
      </div>
      {!marchand.positionConnue && (
        <div
          style={{
            fontSize: 12,
            background: 'var(--rouge-p)',
            color: 'var(--rouge)',
            borderRadius: 8,
            padding: '8px 10px',
            marginBottom: 8,
          }}
        >
          <b>Aucune position.</b> Les commandes sont facturées au palier de livraison le moins cher
          et le livreur ne sait pas où les récupérer.
        </div>
      )}
      <ChoixPosition position={point} surChoix={(lat, lng) => setPoint([lat, lng])} />
      {point && (
        <div style={{ fontSize: 11, color: 'var(--n400)', marginTop: 4 }}>
          {point[0].toFixed(5)}, {point[1].toFixed(5)}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button
          className="btn btn-accent btn-sm"
          disabled={enCours || (!rubriquesModifiees && !positionModifiee)}
          onClick={() => void enregistrer()}
        >
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        {message && (
          <span style={{ fontSize: 12, color: message.erreur ? 'var(--rouge)' : 'var(--vert)' }}>
            {message.texte}
          </span>
        )}
      </div>
    </div>
  )
}

